import {
  ArrowDownwardSharp,
  ArrowUpward,
  FilterAlt,
  Settings,
  Sort,
} from "@mui/icons-material";
import {
  Chip,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ApiChartRankDrection,
  ApiDimension,
  ApiMetric,
  ApiOperator,
} from "@incendium/api";
import GlassCard from "Components/GlassCard/GlassCard";
import { useMemo } from "react";
import { useExplorerContext } from "../providers/ExplorerProvider";
import { isEqual } from "Helpers/objects";
import { ClickableTooltip } from "Components/ClickableTooltip";
import { StyledHelp } from "Components/ClickableTooltip/StyledHelp";
import { useDimensionName } from "features/analytics/hooks/useDimensionName";
import { useMetricName } from "features/analytics/hooks/useMetricName";
import { friendlyOperator } from "Helpers/operators";
import { getAttributionTitle } from "Helpers/getAttributionTitle";
import AnalyticsAttributionIcon from "features/analytics/components/AnalyticsAttributionIcon";
import { ExplorerSidebarSection } from "features/explorer/types";
import { groupBy } from "Helpers/arrays";

type AppliedSetting = {
  key: string;
  section: ExplorerSidebarSection;
  label: string;
  icon?: React.ReactElement;
  tooltip?: string | React.ReactElement;
};

function ExplorerTopBar({
  onOpenSidebar,
  leftSlot,
}: {
  onOpenSidebar: (section?: ExplorerSidebarSection) => void;
  leftSlot?: React.ReactNode;
}) {
  const {
    config,
    configs: initialConfigs,
    type,
    setType,
    dimension,
    selectedRanks,
    having,
    attributes,
    selectedAttributionTypes,
    rankingIndex,
  } = useExplorerContext();
  const dimensionToName = useDimensionName();
  const metricToName = useMetricName();

  const configs = useMemo(() => {
    return Object.fromEntries(
      Object.entries(initialConfigs).filter(([_, cfg]) => !cfg.hidden),
    ) as typeof initialConfigs;
  }, [initialConfigs]);

  const areAllExploreConfigsEqual = useMemo(() => {
    const values = Object.values(configs);
    if (values.length <= 1) return true;
    return values.every((cfg) => isEqual(cfg, values[0]));
  }, [configs]);

  const showInsightFocus =
    Object.keys(configs).length > 1 && !areAllExploreConfigsEqual;

  const appliedSettings = useMemo<AppliedSetting[]>(() => {
    const settings: AppliedSetting[] = [];

    if (
      (config.analyseBy?.grouping?.length || 0) > 1 &&
      dimension !== ApiDimension.DIMENSION_NOT_SET
    ) {
      settings.push({
        key: "dimension",
        section: "analyseBy",
        label: dimensionToName(dimension),
        tooltip: config.analyseBy?.description || "Analyze by",
        icon: <Sort fontSize="small" />,
      });
    }

    const ranking = config.ranking[rankingIndex];
    if (config.ranking.length > 1 && ranking?.label) {
      settings.push({
        key: "ranking-mode",
        section: "rankingMode",
        label: ranking.label,
        tooltip: ranking.tooltip || "Looking for",
      });
    }

    if (selectedRanks.length > 0) {
      settings.push({
        key: "rank-weights",
        section: "rankWeights",
        label:
          selectedRanks.length === 1
            ? metricToName(selectedRanks[0].metric as ApiMetric)
            : `${selectedRanks.length} rank weights`,
        tooltip: selectedRanks
          .map((rank) => {
            const isAsc = rank.direction === ApiChartRankDrection.ASC;
            return `${metricToName(rank.metric as ApiMetric)} (${
              isAsc ? "asc" : "desc"
            })`;
          })
          .join(", "),
        icon:
          selectedRanks.length === 1 &&
          selectedRanks[0].direction === ApiChartRankDrection.ASC ? (
            <ArrowUpward fontSize="small" />
          ) : (
            <ArrowDownwardSharp fontSize="small" />
          ),
      });
    }

    const attributionModels =
      selectedAttributionTypes.length > 0
        ? selectedAttributionTypes
        : config.defaultAttributionTypes || [];

    if (attributionModels.length > 0) {
      settings.push({
        key: "attribution-models",
        section: "attribution",
        label: "Attribution models",
        tooltip: (
          <Stack spacing={0.5}>
            {attributionModels.map((model) => (
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                key={model}
              >
                <AnalyticsAttributionIcon
                  model={model}
                  color="inherit"
                  sx={{
                    width: 32,
                    fill: "currentColor",
                  }}
                />
                <Typography color="inherit" variant="body2">
                  {getAttributionTitle(model)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ),
      });
    }

    having
      .filter(
        (h) => h.value !== undefined && h.value !== null && `${h.value}` !== "",
      )
      .forEach((h) => {
        const havingConfig = config.having?.havings.find(
          (item) => item.metric === h.metric && item.operator === h.operator,
        );
        const label =
          havingConfig?.label ||
          `${metricToName(h.metric as ApiMetric)} ${friendlyOperator(
            h.operator as ApiOperator,
          )} ${h.value}`;
        settings.push({
          key: `having-${h.metric}-${h.operator}`,
          section: "having",
          label: havingConfig?.label
            ? `${havingConfig.label}: ${h.value}`
            : label,
          tooltip: config.having?.description || "Filter",
          icon: <FilterAlt fontSize="small" />,
        });
      });

    const groupedAttr = groupBy(attributes, "key");
    Object.entries(groupedAttr).forEach(([key, attrs]) => {
      const dimensionName = dimensionToName(key as ApiDimension);
      const values = attrs
        .map((a) => `${friendlyOperator(a.operator as ApiOperator)} ${a.value}`)
        .filter(Boolean) as string[];
      const orValues = values.join(" or ");
      settings.push({
        key: `attr-${key}`,
        section: "attributes",
        label: `${dimensionName}`,
        icon: <FilterAlt fontSize="small" />,
        tooltip: `${dimensionName} ${orValues}`,
      });
    });

    return settings;
  }, [
    attributes,
    config.analyseBy,
    config.defaultAttributionTypes,
    config.having,
    config.ranking,
    dimension,
    dimensionToName,
    having,
    metricToName,
    rankingIndex,
    selectedAttributionTypes,
    selectedRanks,
  ]);

  return (
    <GlassCard>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ minWidth: 0, flex: 1 }}
        >
          {showInsightFocus ? (
            <TextField
              label={"Analysis Profile"}
              select
              value={type}
              size="small"
              sx={{ width: 250, flexShrink: 0 }}
              onChange={(e) => setType(e.target.value)}
            >
              {Object.entries(configs).map(([k, v]) => (
                <MenuItem key={k} value={k}>
                  <Tooltip title={v.desc} placement="right" arrow>
                    <Typography sx={{ width: "100%" }}>{v.title}</Typography>
                  </Tooltip>
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Typography variant="h3" noWrap>
              {config.title}
            </Typography>
          )}

          {leftSlot}

          {appliedSettings.length > 0 && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
              sx={{ minWidth: 0 }}
            >
              {appliedSettings.map((setting) => (
                <Tooltip
                  key={setting.key}
                  title={setting.tooltip || ""}
                  arrow
                  disableHoverListener={!setting.tooltip}
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    color="primary"
                    icon={setting.icon}
                    label={setting.label}
                    onClick={() => onOpenSidebar(setting.section)}
                    // sx={{ maxWidth: 220 }}
                  />
                </Tooltip>
              ))}
            </Stack>
          )}
        </Stack>

        <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
          {config.desc && (
            <ClickableTooltip
              text={
                <>
                  {config.desc.split("\n").map((s, i) => (
                    <Typography key={i} variant="body2" color={"white"}>
                      {s}
                    </Typography>
                  ))}
                </>
              }
              placement="top"
              icon={<StyledHelp sx={{ fontSize: 24 }} />}
            />
          )}
          <IconButton onClick={() => onOpenSidebar()}>
            <Settings />
          </IconButton>
        </Stack>
      </Stack>
    </GlassCard>
  );
}

export default ExplorerTopBar;
